import { useRef, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Upload, Loader2 } from "lucide-react";

/**
 * Compact image upload trigger — click to open a file picker, uploads to
 * /api/admin/uploads/image, then calls onUploaded(url).
 */
export default function ImageUploader({
    onUploaded,
    testid = "image-uploader",
    label = "Upload custom image",
}) {
    const inputRef = useRef(null);
    const [busy, setBusy] = useState(false);

    const pick = () => inputRef.current?.click();

    const handleFile = async (e) => {
        const file = e.target.files?.[0];
        e.target.value = ""; // allow re-picking the same file
        if (!file) return;
        if (!file.type.startsWith("image/")) {
            toast.error("Please choose an image file (PNG / JPG / WebP)");
            return;
        }
        if (file.size > 5 * 1024 * 1024) {
            toast.error("Image is too large — please keep under 5 MB");
            return;
        }
        setBusy(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const { data } = await api.post("/admin/uploads/image", fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            toast.success("Image uploaded");
            onUploaded?.(data.url);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setBusy(false);
        }
    };

    return (
        <>
            <input
                ref={inputRef}
                type="file"
                accept="image/png,image/jpeg,image/webp,image/gif"
                onChange={handleFile}
                data-testid={`${testid}-input`}
                className="sr-only"
            />
            <Button
                type="button"
                onClick={pick}
                disabled={busy}
                data-testid={`${testid}-btn`}
                variant="outline"
                className="w-full rounded-full border-navy-100 text-navy-700 hover:bg-navy-50 h-10"
            >
                {busy ? (
                    <>
                        <Loader2 size={14} className="mr-2 animate-spin" />
                        Uploading…
                    </>
                ) : (
                    <>
                        <Upload size={14} className="mr-2" />
                        {label}
                    </>
                )}
            </Button>
        </>
    );
}
